import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import axios from "axios";
import { UiIcon, Svg, CheckMark } from "../Layouts/Icon";
import uiIcons from "../Layouts/Icon/uiIcons";
import { ICON_VIEWBOX, iconLabel, toSvg, toJsx, toVue, toSvelte, toAngular } from "../utils/svgIcon";
import { copyTextToClipboard } from "../utils/formatters";
import { API_URL, ICON_COUNT_TEXT, ICON_LIST_PATH, COMPOSER_PATH, STUDIO_PATH, EXAMPLES_PATH, LICENSE_PATH } from "../constants";
import classes from "./home.scss";


const iconsFor = (query) => `${ICON_LIST_PATH}?q=${encodeURIComponent(query)}`;
// Opens the icon's own panel on the icons page
const iconPage = (icon) =>
  `${ICON_LIST_PATH}?icon=${encodeURIComponent(icon.name)}${icon.d2 ? "&style=duotone" : ""}`;


// The hero's icon field: [icon, word it searches for]. Laid out down both sides, the same
// on the server and in the browser
const FIELD = [
  ["rocket", "rocket"], ["duotone:home", "home"], ["music", "music"], ["leaf", "leaf"],
  ["bicycle", "bicycle"], ["coffee-cup", "coffee"], ["diamond", "diamond"], ["crown", "crown"],
  ["heart", "heart"], ["duotone:video", "video"], ["camera", "camera"], ["globe", "globe"],
  ["umbrella", "umbrella"], ["paper-plane", "plane"], ["fire", "fire"], ["gift", "gift"],
  ["key", "key"], ["flag", "flag"], ["cloud", "cloud"], ["duotone:calendar", "calendar"],
  ["envelope", "envelope"], ["phone", "phone"], ["briefcase", "briefcase"], ["bug", "bug"],
  ["star", "star"], ["bell", "bell"], ["lock", "lock"], ["beach", "beach"],
  ["football", "football"], ["tea", "tea"], ["duotone:cart", "cart"], ["dumbbells", "dumbbell"],
  ["airplane-alt", "airplane"], ["full-sunny", "sun"], ["squirrel", "squirrel"], ["light-bulb", "bulb"],
  ["duotone:brush", "brush"], ["dart", "dart"], ["candy", "candy"], ["duotone:phone", "phone"],
];

const COLUMNS_LEFT = [3, 9.5, 16, 22.5];
const COLUMNS_RIGHT = [77.5, 84, 90.5, 97];
const ROWS = [9, 27, 45, 63, 81];
// Small, fixed offsets so the field looks scattered rather than gridded
const jitter = (index, range) => (((index * 7919) % 97) / 97 - 0.5) * range;
const FIELD_LAYOUT = FIELD.map(([icon, word], index) => {
  const side = index % 2 ? COLUMNS_RIGHT : COLUMNS_LEFT;
  const slot = Math.floor(index / 2);
  return {
    icon,
    word,
    x: side[slot % 4] + jitter(index, 3),
    y: ROWS[Math.floor(slot / 4) % 5] + jitter(index + 3, 8),
    size: 24 + Math.round(((index * 37) % 17)),
    turn: Math.round(jitter(index + 11, 24)),
    drift: 5 + ((index * 13) % 5),
    delay: -((index * 0.7) % 6),
  };
});

const SWITCHER = ["home", "camera", "cart", "envelope", "lock", "gift", "user", "video", "phone", "briefcase", "bug", "flag"];

const COLOURS = [
  { id: "ink", label: "Ink", value: "#1f1142" },
  { id: "violet", label: "Violet", value: "#7c3aed" },
  { id: "teal", label: "Teal", value: "#0f8b8d" },
  { id: "coral", label: "Coral", value: "#e4572e" },
];

// "Use it your way": the same icon in every format, made by the same code as the icon panel
const HOME = { name: "home", isDuotone: false, viewBox: ICON_VIEWBOX, paths: [{ d: uiIcons.home.path, opacity: 1 }] };
const FORMATS = [
  {
    id: "font",
    label: "Web font",
    code: '<link rel="stylesheet" href="icofont/icofont.min.css">\n\n<i class="icofont-home"></i>',
  },
  { id: "svg", label: "SVG", code: toSvg(HOME) },
  { id: "react", label: "React", code: toJsx(HOME) },
  { id: "vue", label: "Vue", code: toVue(HOME) },
  { id: "svelte", label: "Svelte", code: toSvelte(HOME) },
  { id: "angular", label: "Angular", code: toAngular(HOME) },
];

const Arrow = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true" className={classes.arrow}>
    <path d="M3 8h9.5M8.5 4l4 4-4 4" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/** The search box; matching icons drop down over the page as you type */
const HeroSearch = ({ history, query, setQuery, inputRef }) => {
  const [results, setResults] = useState(null); // { q, total, result }
  const [open, setOpen] = useState(false);
  const timer = useRef(null);
  const wrap = useRef(null);
  const q = query.trim();

  useEffect(() => {
    clearTimeout(timer.current);
    if (!q) {
      setResults(null);
      return undefined;
    }
    timer.current = setTimeout(() => {
      axios
        .get(`${API_URL}/api/icons/search?q=${encodeURIComponent(q)}&limit=16`)
        .then(({ data }) => {
          setResults({ q, total: data.total, result: data.result || [] });
          setOpen(true);
        })
        .catch(() => setResults(null));
    }, 200);
    return () => clearTimeout(timer.current);
  }, [q]);

  // Esc or a click elsewhere closes the dropdown; focusing the box opens it again
  useEffect(() => {
    if (!open) return undefined;
    const close = (event) => {
      if (event.type === "keydown" ? event.key === "Escape" : !wrap.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", close);
    };
  }, [open]);

  const submit = (event) => {
    event.preventDefault();
    history.push(q ? iconsFor(q) : ICON_LIST_PATH);
  };

  const showing = open && results && results.q === q;

  return (
    <div className={classes.searchWrap} ref={wrap}>
      <form className={classes.search} role="search" onSubmit={submit}>
        <UiIcon name="search" />
        <input
          ref={inputRef}
          type="search"
          value={query}
          onChange={(event) => {
            setQuery(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          placeholder="Search icons, e.g. arrow, home, cart"
          aria-label="Search icons"
          aria-expanded={!!showing}
          aria-controls="home-search-results"
          autoComplete="off"
          spellCheck="false"
        />
        <button type="submit">Search</button>
      </form>

      {showing ? (
        <div className={classes.results} id="home-search-results" aria-live="polite">
          {results.result.length ? (
            <React.Fragment>
              <ul>
                {results.result.map((icon) => (
                  <li key={`${icon.name}-${icon.code}`}>
                    <Link to={iconPage(icon)} title={iconLabel(icon.name)}>
                      <Svg path={icon.d} pathSecondary={icon.d2} />
                      <span>{iconLabel(icon.name)}</span>
                    </Link>
                  </li>
                ))}
              </ul>
              <Link to={iconsFor(q)} className={classes.resultsAll}>
                See all {results.total} results for “{q}” <Arrow />
              </Link>
            </React.Fragment>
          ) : (
            <p className={classes.noResults}>
              No icons for “{q}” yet. Try a shorter word, or <Link to={STUDIO_PATH}>make one in the studio</Link>.
            </p>
          )}
        </div>
      ) : null}

    </div>
  );
};

HeroSearch.propTypes = {
  history: PropTypes.object.isRequired,
  query: PropTypes.string.isRequired,
  setQuery: PropTypes.func.isRequired,
  inputRef: PropTypes.object,
};

/**
 * Icons drifting down both sides of the hero. They grow and light up as the pointer
 * nears them, like a dock; clicking one searches for it. A mouse nicety: the search box
 * does the same job from the keyboard
 */
const IconField = ({ onPick }) => {
  const field = useRef(null);
  const frame = useRef(0);

  const magnify = (x, y) => {
    frame.current = 0;
    if (!field.current) return;
    field.current.querySelectorAll("button").forEach((item) => {
      const box = item.getBoundingClientRect();
      const distance = Math.hypot(box.left + box.width / 2 - x, box.top + box.height / 2 - y);
      const closeness = Math.max(0, 1 - distance / 170);
      item.style.setProperty("--mag", (1 + closeness * 0.9).toFixed(3));
      item.classList.toggle(classes.near, closeness > 0.55);
    });
  };

  useEffect(() => {
    const hero = field.current && field.current.parentNode;
    if (!hero) return undefined;
    const onMove = (event) => {
      const { clientX, clientY } = event;
      if (!frame.current) frame.current = window.requestAnimationFrame(() => magnify(clientX, clientY));
    };
    const onLeave = () => magnify(-9999, -9999);
    hero.addEventListener("mousemove", onMove);
    hero.addEventListener("mouseleave", onLeave);
    return () => {
      hero.removeEventListener("mousemove", onMove);
      hero.removeEventListener("mouseleave", onLeave);
      if (frame.current) window.cancelAnimationFrame(frame.current);
    };
  }, []);

  return (
    <div className={classes.field} ref={field} aria-hidden="true">
      {FIELD_LAYOUT.map((item) => (
        <button
          key={`${item.icon}-${item.x}`}
          type="button"
          tabIndex={-1}
          title={`Search “${item.word}”`}
          className={classes.fieldIcon}
          style={{ left: `${item.x}%`, top: `${item.y}%`, fontSize: item.size }}
          onClick={() => onPick(item.word)}
        >
          <span style={{ "--turn": `${item.turn}deg`, "--drift": `${item.drift}s`, animationDelay: `${item.delay}s` }}>
            <UiIcon name={item.icon} />
          </span>
        </button>
      ))}
    </div>
  );
};

IconField.propTypes = { onPick: PropTypes.func.isRequired };

/** The hero: headline, search with its dropdown, and the icon field around them */
const Hero = ({ history, categories }) => {
  const [query, setQuery] = useState("");
  const input = useRef(null);
  return (
    <section className={classes.hero}>
      <IconField
        onPick={(word) => {
          setQuery(word);
          if (input.current) input.current.focus();
        }}
      />
      <div className={classes.heroInner}>
        <h1>
          Icons for <span>everything</span> you build.
        </h1>
        <p className={classes.lead}>
          Line, solid and duotone icons, all drawn on one 24px grid. Search, copy, done.
        </p>
        <HeroSearch history={history} query={query} setQuery={setQuery} inputRef={input} />
        <ul className={classes.facts}>
          <li>
            <strong>{ICON_COUNT_TEXT}</strong> icons
          </li>
          <li>
            <strong>3</strong> styles
          </li>
          <li>
            <strong>{categories.length || 31}</strong> categories
          </li>
          <li>
            <Link to={LICENSE_PATH}>
              <strong>Free</strong> for commercial use
            </Link>
          </li>
        </ul>
      </div>
    </section>
  );
};

Hero.propTypes = {
  history: PropTypes.object.isRequired,
  categories: PropTypes.array.isRequired,
};

/** The same icons in regular and duotone, in a colour of your choice */
const StyleSwitcher = () => {
  const [duotone, setDuotone] = useState(true);
  const [colour, setColour] = useState(COLOURS[1]);
  return (
    <div className={classes.switcher}>
      <div className={classes.switcherBar}>
        <div className="segmented" role="group" aria-label="Style">
          <button type="button" aria-pressed={!duotone} onClick={() => setDuotone(false)}>
            Regular
          </button>
          <button type="button" aria-pressed={duotone} onClick={() => setDuotone(true)}>
            Duotone
          </button>
        </div>
        <div className={classes.swatches} role="group" aria-label="Colour">
          {COLOURS.map((item) => (
            <button
              key={item.id}
              type="button"
              aria-pressed={item.id === colour.id}
              aria-label={item.label}
              title={item.label}
              style={{ background: item.value }}
              onClick={() => setColour(item)}
            />
          ))}
        </div>
      </div>
      <ul className={classes.switcherGrid} style={{ color: colour.value }} aria-label={duotone ? "Duotone icons" : "Regular icons"}>
        {SWITCHER.map((name) => (
          <li key={name}>
            <UiIcon name={duotone ? `duotone:${name}` : name} />
            <span>{name}</span>
          </li>
        ))}
      </ul>
    </div>
  );
};

/** One icon, every way to use it */
const Formats = () => {
  const [format, setFormat] = useState(FORMATS[0]);
  const [copied, setCopied] = useState(false);
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const copy = () => {
    copyTextToClipboard(format.code);
    setCopied(true);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setCopied(false), 1600);
  };
  return (
    <div className={classes.formats}>
      <div className={classes.tabs} role="tablist" aria-label="Ways to use an icon">
        {FORMATS.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={item.id === format.id}
            onClick={() => {
              setFormat(item);
              setCopied(false);
            }}
          >
            {item.label}
          </button>
        ))}
      </div>
      <div className={classes.code} role="tabpanel">
        <button type="button" className={copied ? `${classes.copy} ${classes.copied}` : classes.copy} onClick={copy}>
          {copied ? (
            <React.Fragment>
              <CheckMark /> Copied
            </React.Fragment>
          ) : (
            "Copy"
          )}
        </button>
        <pre>
          <code>{format.code}</code>
        </pre>
      </div>
    </div>
  );
};

export const Home = ({ history, categories }) => (
  <main className={classes.page}>
    <Helmet>
      <title>{`IcoFont: ${ICON_COUNT_TEXT} Free Icons as Icon Font, SVG and Duotone`}</title>
      <meta
        name="description"
        content={`${ICON_COUNT_TEXT} free icons in line, solid and duotone. Search, copy as SVG or components, use the icon font, build your own font, or design your own icons.`}
      />
    </Helmet>

    <Hero history={history} categories={categories} />

    {/* Styles */}
    <section className={classes.section}>
      <div className={classes.split}>
        <div className={classes.copyBlock}>
          <span className={classes.kicker}>Styles</span>
          <h2>One icon, more than one look.</h2>
          <p>
            Line, solid and 162 duotone icons, many of them in more than one style. Duotone’s second tone is the
            same colour at 60%, so the icons match whatever colour you give them.
          </p>
          <Link to={ICON_LIST_PATH} className={classes.more}>
            Browse by style <Arrow />
          </Link>
        </div>
        <StyleSwitcher />
      </div>
    </section>

    {/* Formats */}
    <section className={`${classes.section} ${classes.tinted}`}>
      <div className={classes.split}>
        <div className={classes.copyBlock}>
          <span className={classes.kicker}>Use it your way</span>
          <h2>A font, an SVG, or a component.</h2>
          <p>
            Every icon copies as SVG or as a React, Vue, Svelte or Angular component, straight from its panel. Or link
            one stylesheet and set icons like text.
          </p>
          <Link to={EXAMPLES_PATH} className={classes.more}>
            See the examples <Arrow />
          </Link>
        </div>
        <Formats />
      </div>
    </section>

    {/* Tools */}
    <section className={classes.section}>
      <div className={classes.center}>
        <span className={classes.kicker}>Make it yours</span>
        <h2>Only the icons you need. And the ones you don’t have yet.</h2>
      </div>
      <div className={classes.panels}>
        <Link to={COMPOSER_PATH} className={`${classes.panel} ${classes.panelComposer}`}>
          <div className={classes.panelText}>
            <h3>Composer</h3>
            <p>Pick the icons you use and download them as your own font, with your name and class prefix.</p>
            <span className={classes.more}>
              Build your font <Arrow />
            </span>
          </div>
          <div className={classes.fontCard} aria-hidden="true">
            <div className={classes.fontHead}>
              <strong>my-icons.woff2</strong>
              <span>8 icons</span>
            </div>
            <div className={classes.fontGrid}>
              {["home", "search", "heart", "cart", "calendar", "envelope", "globe", "lock"].map((name) => (
                <span key={name}>
                  <UiIcon name={name} />
                </span>
              ))}
            </div>
          </div>
        </Link>
        <Link to={STUDIO_PATH} className={`${classes.panel} ${classes.panelStudio}`}>
          <div className={classes.panelText}>
            <h3>
              Studio <span className={classes.tag}>New</span>
            </h3>
            <p>Combine icons, shapes and badges into new icons, edit them part by part, and add them to your font.</p>
            <span className={classes.more}>
              Open the studio <Arrow />
            </span>
          </div>
          <div className={classes.studioCard} aria-hidden="true">
            <span className={classes.studioIcon}>
              <UiIcon name="user-alt-4" />
            </span>
            <span className={classes.studioBadge}>
              <UiIcon name="plus-circle" />
            </span>
          </div>
        </Link>
      </div>
    </section>

    {/* Categories */}
    {categories.length ? (
      <section className={classes.section}>
        <div className={classes.center}>
          <span className={classes.kicker}>Categories</span>
          <h2>Something for every corner of your app.</h2>
        </div>
        <ul className={classes.categories}>
          {categories.map((category) => (
            <li key={category.cat_name}>
              <Link to={`${ICON_LIST_PATH}?category=${category.cat_name}`}>
                {iconLabel(category.cat_name)}
                <span>{category.total}</span>
              </Link>
            </li>
          ))}
        </ul>
      </section>
    ) : null}

    {/* Closing */}
    <section className={classes.section}>
      <div className={classes.cta}>
        <h2>Find your next icon.</h2>
        <p>Free for personal and commercial projects. No sign-up.</p>
        <div className={classes.ctaActions}>
          <Link to={ICON_LIST_PATH} className={classes.ctaPrimary}>
            Browse icons
          </Link>
          <a href="/process/download?type=1&uid=icofont" className={classes.ctaSecondary}>
            <UiIcon name="download" /> Download IcoFont
          </a>
        </div>
      </div>
    </section>
  </main>
);

Home.propTypes = {
  history: PropTypes.object.isRequired,
  categories: PropTypes.array.isRequired,
};

export default Home;
